/**
 * Trading Bot — раздел Beta.
 *
 * Вкладки: Overview · Settings · Trades · Logs · Weex Options
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Page } from '@/components/Page';
import { Section } from '@/components/Section';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/Spinner';
import { Empty } from '@/components/Empty';
import { sb } from '@/hooks/useSupabaseQuery';
import { useSupabaseQuery } from '@/hooks/useSupabaseQuery';
import { useAuth } from '@/providers/AuthProvider';
import { useHaptic } from '@/hooks/useHaptic';

// ── Types ────────────────────────────────────────────────────────────────────

type TabId = 'overview' | 'settings' | 'trades' | 'logs' | 'options';
type ExchangeName = 'fxpro' | 'vantage' | 'weex';
type Timeframe = '1H' | '15M';

interface BotExchange {
  id: number;
  exchange: ExchangeName;
  is_demo: boolean;
  is_enabled: boolean;
  status: 'online' | 'offline' | 'error' | 'not_configured';
  ws_status?: 'connected' | 'reconnecting' | 'fallback-polling' | null;
  last_sync_at: string | null;
  last_error: string | null;
  balance_json: { currency: string; available: number; equity: number } | null;
  api_key: string | null;
  api_secret: string | null;
  mt_login: string | null;
  mt_server: string | null;
}

interface BotSettings {
  id: number;
  exchange: ExchangeName;
  timeframe: Timeframe;
  is_enabled: boolean;
  adr_multiplier: number;
  body_shadow_ratio: number;
  entry_offset_ticks: number;
  sl_offset_ticks: number;
  tp_multiplier: number;
  candles_timeout: number;
  close_slippage_ticks: number;
  risk_type: 'usd' | 'pct';
  risk_value: number;
  max_trades_day: number;
  adr_period: number;
  polling_fallback_s: number;
  tick_size_override_enabled: boolean;
  tick_size_override: number | null;
}

interface BotSymbol {
  exchange: ExchangeName;
  symbol: string;
  tick_size: number;
  updated_at: string;
}

interface BotTrade {
  id: number;
  exchange: ExchangeName;
  symbol: string;
  timeframe: Timeframe;
  side: 'buy' | 'sell';
  entry_price: number;
  stop_loss: number;
  take_profit: number;
  exit_price: number | null;
  exit_reason: string | null;
  pnl_usd: number | null;
  pnl_pct: number | null;
  risk_usd: number | null;
  adr_at_signal: number | null;
  body_shadow_ratio: number | null;
  opened_at: string;
  closed_at: string | null;
  status: 'open' | 'closed';
}

interface BotLog {
  id: number;
  level: 'debug' | 'info' | 'warn' | 'error';
  source: string;
  exchange: string | null;
  symbol: string | null;
  timeframe: string | null;
  message: string;
  payload: unknown;
  correlation_id: string | null;
  created_at: string;
}

interface WeexOptionsSnapshot {
  id: number;
  signal_id: number | null;
  symbol: string;
  snapshot_t: string;
  strikes_json: Array<{
    strike: number;
    call_bid: number;
    call_ask: number;
    put_bid: number;
    put_ask: number;
  }>;
  taken_at: string;
}

// ── Constants ────────────────────────────────────────────────────────────────

const EXCHANGES: { id: ExchangeName; label: string; isDemo: boolean }[] = [
  { id: 'fxpro',   label: 'FxPro',   isDemo: true },
  { id: 'vantage', label: 'Vantage', isDemo: true },
  { id: 'weex',    label: 'Weex',    isDemo: false },
];

const TIMEFRAMES: Timeframe[] = ['1H', '15M'];

const DEFAULT_SETTINGS: Omit<BotSettings, 'id' | 'exchange' | 'timeframe'> = {
  is_enabled: false,
  adr_multiplier: 2.0,
  body_shadow_ratio: 0.6667,
  entry_offset_ticks: 50,
  sl_offset_ticks: 1,
  tp_multiplier: 1.5,
  candles_timeout: 4,
  close_slippage_ticks: 50,
  risk_type: 'pct',
  risk_value: 1.0,
  max_trades_day: 5,
  adr_period: 14,
  polling_fallback_s: 5,
  tick_size_override_enabled: false,
  tick_size_override: null,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDt(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru', { timeZone: 'UTC', hour12: false });
}

function fmtNum(v: number | null | undefined, dec = 2): string {
  if (v == null) return '—';
  return v.toFixed(dec);
}

function compactErrorMessage(message: string | null | undefined): string | null {
  if (!message) return null;
  if (message.includes('<!DOCTYPE html') || message.includes('<html')) {
    const httpStatus = message.match(/HTTP\s*\d{3}/i)?.[0] ?? null;
    return httpStatus ? `${httpStatus}. Детали в console.error` : 'Ошибка API. Детали в console.error';
  }
  const firstLine = message.split('\n')[0]?.trim() ?? '';
  if (!firstLine) return null;
  return firstLine.length > 180 ? `${firstLine.slice(0, 180)}…` : firstLine;
}

/** Возвращает ссылку на документацию по конкретной ошибке, если она известна. */
function docsLinkForError(error: string | null | undefined): string | null {
  if (!error) return null;
  const normalizedError = error.toLowerCase();
  if (error.includes('MT5_BRIDGE_URL')) return 'https://github.com/ragevoyage-boop/sword#подключение-mt5-bridge-fxpro--vantage';
  if (
    error.includes('WEEX_BASE_URL')
    || normalizedError.includes('weex host unreachable')
    || normalizedError.includes('enotfound')
  ) {
    return 'https://github.com/ragevoyage-boop/sword#weex-host-unreachable-enotfound';
  }
  return null;
}

function statusColor(status: string): string {
  if (status === 'online') return '#34c759';
  if (status === 'error') return 'var(--tg-destructive)';
  if (status === 'not_configured') return '#ff9500';
  return 'var(--tg-hint)';
}

function levelColor(level: string): string {
  if (level === 'error') return 'var(--tg-destructive)';
  if (level === 'warn') return '#ff9500';
  if (level === 'info') return 'var(--tg-button)';
  return 'var(--tg-hint)';
}

// ── Tab bar ──────────────────────────────────────────────────────────────────

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'settings', label: 'Settings' },
  { id: 'trades',   label: 'Trades' },
  { id: 'logs',     label: 'Logs' },
  { id: 'options',  label: 'Options' },
];

function TabBar({ active, onChange }: { active: TabId; onChange: (t: TabId) => void }): JSX.Element {
  return (
    <div style={{ display: 'flex', margin: '0 var(--space-md) var(--space-md)', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '0.5px solid var(--tg-separator)' }}>
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          style={{ flex: 1, minHeight: 36, border: 'none', background: active === t.id ? 'var(--tg-button)' : 'transparent', color: active === t.id ? 'var(--tg-button-text)' : 'var(--tg-text)', fontSize: 12, cursor: 'pointer', fontWeight: active === t.id ? 600 : 400 }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab({ userId, onNavigateToSettings }: { userId: string; onNavigateToSettings: () => void }): JSX.Element {
  const { notify } = useHaptic();
  const [toggling, setToggling] = useState<ExchangeName | null>(null);
  const [optimisticEnabled, setOptimisticEnabled] = useState<Partial<Record<ExchangeName, boolean>>>({});
  const [toggleError, setToggleError] = useState<string | null>(null);
  const loggedErrorsRef = useRef(new Set<string>());

  const exchanges = useSupabaseQuery<BotExchange[]>(
    () => sb.from('bot_exchanges').select('*').eq('user_id', userId).order('exchange'),
    [userId],
    30_000,
  );

  async function toggleExchange(exchange: ExchangeName, enabled: boolean): Promise<void> {
    setToggling(exchange);
    setToggleError(null);
    // Оптимистичное обновление: переключатель меняется сразу, при ошибке — откат.
    setOptimisticEnabled((prev) => ({ ...prev, [exchange]: enabled }));
    const { error } = await sb.from('bot_exchanges').upsert({
      user_id: userId,
      exchange,
      is_enabled: enabled,
      ...(enabled ? {} : { status: 'offline', last_error: null }),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,exchange' });
    setToggling(null);
    if (error) {
      console.error(`[Beta][${exchange}] toggle error:`, error.message);
      setOptimisticEnabled((prev) => {
        const next = { ...prev };
        delete next[exchange];
        return next;
      });
      setToggleError(`Не удалось ${enabled ? 'включить' : 'выключить'} ${exchange}: ${error.message}`);
      notify('error');
      return;
    }
    exchanges.refetch();
    notify('success');
  }

  async function handleRefresh(exchange: ExchangeName): Promise<void> {
    await sb.from('bot_exchanges').update({ updated_at: new Date().toISOString() })
      .eq('user_id', userId).eq('exchange', exchange);
    exchanges.refetch();
  }

  const rows = useMemo(() => exchanges.data ?? [], [exchanges.data]);

  // Снимаем оптимистичные значения, когда сервер вернул то же состояние.
  useEffect(() => {
    setOptimisticEnabled((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [exchange, value] of Object.entries(prev) as [ExchangeName, boolean][]) {
        const row = rows.find((r) => r.exchange === exchange);
        if ((row?.is_enabled ?? false) === value) {
          delete next[exchange];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [rows]);
  useEffect(() => {
    for (const row of rows) {
      if (row.last_error) {
        const key = `${row.exchange}:${row.last_error}`;
        if (loggedErrorsRef.current.has(key)) continue;
        loggedErrorsRef.current.add(key);
        console.error(`[Beta][${row.exchange}] full error:`, row.last_error);
      }
    }
  }, [rows]);

  if (exchanges.isLoading) return <Spinner />;

  return (
    <>
      {toggleError && (
        <div style={{ padding: 'var(--space-sm) var(--space-lg)', color: 'var(--tg-destructive)', fontSize: 13 }}>
          ⚠️ {toggleError}
        </div>
      )}
      {exchanges.error && (
        <div style={{ padding: 'var(--space-sm) var(--space-lg)', color: 'var(--tg-destructive)', fontSize: 13 }}>
          ⚠️ Не удалось загрузить биржи: {exchanges.error}
        </div>
      )}
      {EXCHANGES.map(({ id, label, isDemo }) => {
        const row = rows.find((r) => r.exchange === id);
        const bal = row?.balance_json;
        const shortError = compactErrorMessage(row?.last_error);
        const isMt5Broker = id === 'fxpro' || id === 'vantage';
        const isEnabled = optimisticEnabled[id] ?? row?.is_enabled ?? false;
        const isTogglingThis = toggling === id;

        // Determine missing-credentials placeholder
        let missingCredentialsHint: string | null = null;
        if (isMt5Broker) {
          if (!row?.mt_login || !row?.mt_server) {
            missingCredentialsHint = 'Укажите MT5 login / server в Настройках → API ключи';
          }
        } else {
          if (!row?.api_key || !row?.api_secret) {
            missingCredentialsHint = 'Укажите API key / secret Weex в Настройках → API ключи';
          }
        }

        const isNotConfigured = row?.status === 'not_configured';
        const errorDocsLink = docsLinkForError(row?.last_error);
        const hasInlineError = !isNotConfigured && !!shortError && !!errorDocsLink;
        const showFooterError = !!shortError && !isNotConfigured && !hasInlineError;

        return (
          <Section
            key={id}
            header={`${label}${isDemo ? ' (Demo)' : ' (Real)'}`}
            footer={showFooterError ? `Ошибка: ${shortError}` : undefined}
          >
            <div style={{ padding: 'var(--space-md) var(--space-lg)' }}>
              {/* Status row with toggle */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--tg-hint)', fontSize: 13 }}>Статус</span>
                  <button
                    type="button"
                    onClick={() => void handleRefresh(id)}
                    style={{ fontSize: 11, color: 'var(--tg-button)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }}
                  >
                    Обновить
                  </button>
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {isMt5Broker && (
                    <span
                      style={{
                        fontSize: 10,
                        padding: '2px 6px',
                        borderRadius: 999,
                        border: '0.5px solid var(--tg-separator)',
                        color: 'var(--tg-hint)',
                      }}
                    >
                      via MT5 bridge
                    </span>
                  )}
                  <span style={{ color: statusColor(row?.status ?? 'offline'), fontSize: 13, fontWeight: 600 }}>
                    {row ? row.status.replace(/_/g, ' ').toUpperCase() : 'OFFLINE'}
                  </span>
                </span>
              </div>

              {/* Enable/Disable toggle */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ color: 'var(--tg-hint)', fontSize: 13 }}>
                  {isEnabled ? 'Включён' : 'Выключен'}
                </span>
                <label style={{ display: 'inline-flex', alignItems: 'center', cursor: isTogglingThis ? 'wait' : 'pointer' }}>
                  <input
                    type="checkbox"
                    aria-label={`${isEnabled ? 'Выключить' : 'Включить'} ${label}`}
                    checked={isEnabled}
                    disabled={isTogglingThis}
                    onChange={(e) => void toggleExchange(id, e.target.checked)}
                    style={{ width: 36, height: 20, cursor: isTogglingThis ? 'wait' : 'pointer' }}
                  />
                </label>
              </div>

              {/* WebSocket row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: 'var(--tg-hint)', fontSize: 13 }}>WebSocket</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {String(row?.ws_status ?? 'fallback-polling')}
                </span>
              </div>

              {/* Last sync */}
              {row?.last_sync_at && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: 'var(--tg-hint)', fontSize: 13 }}>Синхронизация</span>
                  <span style={{ fontSize: 13 }}>{fmtDt(row.last_sync_at)}</span>
                </div>
              )}

              {/* Balance — always show row when exchange record exists */}
              {row && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: 'var(--tg-hint)', fontSize: 13 }}>Баланс</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: bal ? undefined : 'var(--tg-hint)' }}>
                      {bal ? `${fmtNum(bal.available)} ${bal.currency}` : '—'}
                    </span>
                  </div>
                  {bal && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ color: 'var(--tg-hint)', fontSize: 13 }}>Equity</span>
                      <span style={{ fontSize: 13 }}>
                        {fmtNum(bal.equity)} {bal.currency}
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Not-configured error block */}
              {isNotConfigured && shortError && (
                <div
                  style={{
                    marginTop: 8,
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: 'rgba(255, 149, 0, 0.12)',
                    border: '0.5px solid rgba(255, 149, 0, 0.35)',
                  }}
                >
                  <div style={{ fontSize: 12, color: '#ff9500', marginBottom: 6, wordBreak: 'break-word' }}>
                    {shortError}
                  </div>
                  <button
                    type="button"
                    onClick={onNavigateToSettings}
                    style={{
                      fontSize: 12,
                      color: '#ff9500',
                      background: 'none',
                      border: '0.5px solid rgba(255, 149, 0, 0.5)',
                      borderRadius: 6,
                      padding: '3px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    Настроить →
                  </button>
                  {errorDocsLink && (
                    <a
                      href={errorDocsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'block', marginTop: 6, fontSize: 11, color: 'var(--tg-hint)', textDecoration: 'none' }}
                    >
                      Что это значит? →
                    </a>
                  )}
                </div>
              )}

              {/* Inline error block with docs link (e.g. Weex ENOTFOUND) */}
              {hasInlineError && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--tg-hint)', marginBottom: 4, wordBreak: 'break-word' }}>
                    {`Ошибка: ${shortError}`}
                  </div>
                  <a
                    href={errorDocsLink!}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 11, color: 'var(--tg-hint)', textDecoration: 'none' }}
                  >
                    Что это значит? →
                  </a>
                </div>
              )}

              {/* Missing credentials hint */}
              {missingCredentialsHint && !isNotConfigured && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--tg-hint)' }}>
                  {missingCredentialsHint}
                </div>
              )}
            </div>
          </Section>
        );
      })}
    </>
  );
}

// ── Settings tab ─────────────────────────────────────────────────────────────

function SettingsTab({ userId }: { userId: string }): JSX.Element {
  const { notify } = useHaptic();
  const [saveStatus, setSaveStatus] = useState<Map<string, 'saving' | 'saved' | 'error'>>(() => new Map());
  const debounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [tfSettings, setTfSettings] = useState<
    Record<ExchangeName, Record<Timeframe, typeof DEFAULT_SETTINGS>>
  >(() => {
    const out = {} as Record<ExchangeName, Record<Timeframe, typeof DEFAULT_SETTINGS>>;
    for (const e of EXCHANGES) {
      out[e.id] = {} as Record<Timeframe, typeof DEFAULT_SETTINGS>;
      for (const tf of TIMEFRAMES) {
        out[e.id][tf] = { ...DEFAULT_SETTINGS };
      }
    }
    return out;
  });

  // Keep a ref always pointing to the latest settings so debounced saves read fresh data
  const tfSettingsRef = useRef(tfSettings);
  useEffect(() => {
    tfSettingsRef.current = tfSettings;
  });

  // Load existing config
  const existing = useSupabaseQuery(
    () =>
      Promise.all([
        sb.from('bot_settings').select('*').eq('user_id', userId),
        sb.from('bot_symbols').select('exchange, symbol, tick_size, updated_at').eq('user_id', userId),
      ]).then(([settingsRes, symbolsRes]) => ({
        data: { settings: settingsRes.data ?? [], symbols: symbolsRes.data ?? [] },
        error: settingsRes.error ?? symbolsRes.error,
      })),
    [userId],
  );

  // Initialize forms once when data first arrives
  useEffect(() => {
    if (!existing.data) return;
    const { settings } = existing.data as {
      settings: BotSettings[];
    };

    setTfSettings(() => {
      const newTf: Record<ExchangeName, Record<Timeframe, typeof DEFAULT_SETTINGS>> = {
        fxpro:   { '1H': { ...DEFAULT_SETTINGS }, '15M': { ...DEFAULT_SETTINGS } },
        vantage: { '1H': { ...DEFAULT_SETTINGS }, '15M': { ...DEFAULT_SETTINGS } },
        weex:    { '1H': { ...DEFAULT_SETTINGS }, '15M': { ...DEFAULT_SETTINGS } },
      };
      for (const row of settings) {
        if (newTf[row.exchange] == null) continue;
        newTf[row.exchange][row.timeframe] = {
          is_enabled: row.is_enabled,
          adr_multiplier: row.adr_multiplier,
          body_shadow_ratio: row.body_shadow_ratio,
          entry_offset_ticks: row.entry_offset_ticks,
          sl_offset_ticks: row.sl_offset_ticks,
          tp_multiplier: row.tp_multiplier,
          candles_timeout: row.candles_timeout,
          close_slippage_ticks: row.close_slippage_ticks,
          risk_type: row.risk_type,
          risk_value: row.risk_value,
          max_trades_day: row.max_trades_day,
          adr_period: row.adr_period,
          polling_fallback_s: Number(row.polling_fallback_s ?? 5),
          tick_size_override_enabled: Boolean(row.tick_size_override_enabled),
          tick_size_override: Number(row.tick_size_override ?? 0) || null,
        };
      }
      return newTf;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.data]);

  /**
   * Persist settings for one (exchange, timeframe) pair.
   * When `settingsOverride` is provided it is used directly (needed for is_enabled
   * which must save before React flushes the state update).
   */
  const doSave = useCallback(async (
    exch: ExchangeName,
    tf: Timeframe,
    settingsOverride?: typeof DEFAULT_SETTINGS,
  ): Promise<void> => {
    const key = `${exch}:${tf}`;
    setSaveStatus((prev) => new Map(prev).set(key, 'saving'));
    const s = settingsOverride ?? tfSettingsRef.current[exch][tf];
    const { error } = await sb.from('bot_settings').upsert({
      user_id: userId,
      exchange: exch,
      timeframe: tf,
      ...s,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,exchange,timeframe' });
    if (error) {
      console.error(error);
      notify('error');
      setSaveStatus((prev) => new Map(prev).set(key, 'error'));
    } else {
      notify('success');
      setSaveStatus((prev) => new Map(prev).set(key, 'saved'));
      setTimeout(() => {
        setSaveStatus((prev) => {
          const next = new Map(prev);
          if (next.get(key) === 'saved') next.delete(key);
          return next;
        });
      }, 1500);
    }
  }, [userId, notify]);

  const updateTf = (exch: ExchangeName, tf: Timeframe, field: string, value: string | boolean): void => {
    const coerced = typeof value === 'boolean' ? value : isNaN(Number(value)) ? value : Number(value);
    const newSettings = { ...tfSettings[exch][tf], [field]: coerced } as typeof DEFAULT_SETTINGS;
    setTfSettings((p) => ({
      ...p,
      [exch]: { ...p[exch], [tf]: newSettings },
    }));

    const key = `${exch}:${tf}`;
    // Cancel any pending debounce for this pair
    const pending = debounceTimers.current.get(key);
    if (pending != null) clearTimeout(pending);
    debounceTimers.current.delete(key);

    if (field === 'is_enabled') {
      // Save immediately — pass newSettings directly since state hasn't flushed yet
      void doSave(exch, tf, newSettings);
    } else {
      // Debounced save — by the time 600 ms passes the ref will be up to date
      setSaveStatus((prev) => new Map(prev).set(key, 'saving'));
      debounceTimers.current.set(key, setTimeout(() => {
        debounceTimers.current.delete(key);
        void doSave(exch, tf);
      }, 600));
    }
  };

  if (existing.isLoading) return <Spinner />;
  const symbols = (existing.data as { symbols: BotSymbol[] } | undefined)?.symbols ?? [];
  // Base style for the per-section autosave status indicator; only the color varies.
  const indBase: React.CSSProperties = { fontSize: 10, fontWeight: 400, textTransform: 'none', letterSpacing: 0 };

  return (
    <>
      {EXCHANGES.map(({ id, label }) => (
        <div key={id}>
          {TIMEFRAMES.map((tf) => {
            const s = tfSettings[id][tf];
            const saveKey = `${id}:${tf}`;
            const st = saveStatus.get(saveKey);
            const preferredSymbol = id === 'weex' ? 'BTCUSDT' : 'EURUSD';
            const autoTick = symbols.find((row) => row.exchange === id && row.symbol === preferredSymbol)
              ?? symbols.find((row) => row.exchange === id);
            return (
              <Section
                key={tf}
                header={
                  <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{`${label} / ${tf} — Параметры`}</span>
                    {st === 'saving' && <span style={{ ...indBase, color: 'var(--tg-hint)' }}>⌛ Сохраняем…</span>}
                    {st === 'saved' && <span style={{ ...indBase, color: '#34c759' }}>💾 Сохранено</span>}
                    {st === 'error' && <span style={{ ...indBase, color: 'var(--tg-destructive)' }}>⚠ Ошибка</span>}
                  </span>
                }
              >
                <label style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-sm) var(--space-lg)' }}>
                  <span>Включить</span>
                  <input type="checkbox" checked={s.is_enabled} onChange={(e) => updateTf(id, tf, 'is_enabled', e.target.checked)} />
                </label>
                <Input label="Множитель ADR" value={String(s.adr_multiplier)} onChange={(e) => updateTf(id, tf, 'adr_multiplier', e.target.value)} />
                <Input label="Body/Shadow ratio" value={String(s.body_shadow_ratio)} onChange={(e) => updateTf(id, tf, 'body_shadow_ratio', e.target.value)} />
                <Input label="Отступ entry (тики)" value={String(s.entry_offset_ticks)} onChange={(e) => updateTf(id, tf, 'entry_offset_ticks', e.target.value)} />
                <Input label="Отступ SL (тики)" value={String(s.sl_offset_ticks)} onChange={(e) => updateTf(id, tf, 'sl_offset_ticks', e.target.value)} />
                <Input label="Множитель TP" value={String(s.tp_multiplier)} onChange={(e) => updateTf(id, tf, 'tp_multiplier', e.target.value)} />
                <Input label="Тайм-аут (свечи)" value={String(s.candles_timeout)} onChange={(e) => updateTf(id, tf, 'candles_timeout', e.target.value)} />
                <Input label="Слиппедж закрытия (тики)" value={String(s.close_slippage_ticks)} onChange={(e) => updateTf(id, tf, 'close_slippage_ticks', e.target.value)} />
                <div style={{ padding: 'var(--space-sm) var(--space-lg)', display: 'flex', gap: 8 }}>
                  <select
                    value={s.risk_type}
                    onChange={(e) => updateTf(id, tf, 'risk_type', e.target.value)}
                    style={{ flex: 1, padding: '6px 8px', borderRadius: 8, border: '0.5px solid var(--tg-separator)', background: 'var(--tg-section-bg)', color: 'var(--tg-text)' }}
                  >
                    <option value="pct">% от баланса</option>
                    <option value="usd">USD</option>
                  </select>
                  <input
                    type="number"
                    value={s.risk_value}
                    onChange={(e) => updateTf(id, tf, 'risk_value', e.target.value)}
                    style={{ flex: 1, padding: '6px 8px', borderRadius: 8, border: '0.5px solid var(--tg-separator)', background: 'var(--tg-section-bg)', color: 'var(--tg-text)' }}
                    placeholder="Риск"
                  />
                </div>
                <Input label="Макс. сделок/день" value={String(s.max_trades_day)} onChange={(e) => updateTf(id, tf, 'max_trades_day', e.target.value)} />
                <Input label="Период ADR" value={String(s.adr_period)} onChange={(e) => updateTf(id, tf, 'adr_period', e.target.value)} />
                <Input label="Повтор запроса свечи (сек)" value={String(s.polling_fallback_s)} onChange={(e) => updateTf(id, tf, 'polling_fallback_s', e.target.value)} />
                <div style={{ padding: '0 var(--space-lg) var(--space-sm)', color: 'var(--tg-hint)', fontSize: 12 }}>
                  Используется только если WebSocket-соединение с биржей разорвано. В нормальном режиме сканирование происходит онлайн на каждом тике.
                </div>
                <label style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--space-sm) var(--space-lg)' }}>
                  <span>Override tick size</span>
                  <input
                    type="checkbox"
                    checked={s.tick_size_override_enabled}
                    onChange={(e) => updateTf(id, tf, 'tick_size_override_enabled', e.target.checked)}
                  />
                </label>
                {!s.tick_size_override_enabled ? (
                  <div style={{ padding: '0 var(--space-lg) var(--space-sm)', color: 'var(--tg-hint)', fontSize: 12 }}>
                    Размер тика: auto {autoTick ? `${autoTick.tick_size} (${autoTick.symbol})` : '—'}
                  </div>
                ) : (
                  <Input
                    label="Размер тика (override)"
                    value={String(s.tick_size_override ?? '')}
                    onChange={(e) => updateTf(id, tf, 'tick_size_override', e.target.value)}
                  />
                )}
                <div style={{ padding: '0 var(--space-lg) var(--space-sm)', color: 'var(--tg-hint)', fontSize: 12 }}>
                  Минимальный шаг цены инструмента. По умолчанию подтягивается автоматически из спецификации символа на бирже. Используется во всех расчётах: вход = Close ± N тиков, SL = за тенью на 1 тик, аварийное закрытие = ±50 тиков.
                </div>
              </Section>
            );
          })}
        </div>
      ))}
    </>
  );
}

// ── Trades tab ───────────────────────────────────────────────────────────────

function TradesTab({ userId }: { userId: string }): JSX.Element {
  const [filterExch, setFilterExch] = useState('all');
  const [filterTf, setFilterTf] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const trades = useSupabaseQuery<BotTrade[]>(
    () =>
      sb
        .from('bot_trades')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(200),
    [userId],
    15_000,
  );

  const all = trades.data ?? [];
  const filtered = all.filter((t) => {
    if (filterExch !== 'all' && t.exchange !== filterExch) return false;
    if (filterTf !== 'all' && t.timeframe !== filterTf) return false;
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    return true;
  });

  // Stats
  // Неисполненные (expired) заявки — не сделки: не учитываем их в винрейте.
  const closed = filtered.filter((t) => t.status === 'closed' && t.exit_reason !== 'expired');
  const wins = closed.filter((t) => (t.pnl_usd ?? 0) > 0);
  const totalPnl = closed.reduce((s, t) => s + (t.pnl_usd ?? 0), 0);
  const winRate = closed.length > 0 ? (wins.length / closed.length) * 100 : 0;

  const exportCsv = useCallback((): void => {
    const header = 'id,exchange,symbol,tf,side,entry,sl,tp,exit,reason,pnl_usd,pnl_pct,risk,opened_at,closed_at\n';
    const rows = filtered.map((t) =>
      [t.id, t.exchange, t.symbol, t.timeframe, t.side, t.entry_price, t.stop_loss, t.take_profit,
        t.exit_price ?? '', t.exit_reason ?? '', t.pnl_usd ?? '', t.pnl_pct ?? '', t.risk_usd ?? '',
        t.opened_at, t.closed_at ?? ''].join(','),
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bot_trades_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  if (trades.isLoading) return <Spinner />;

  const selectStyle: React.CSSProperties = { padding: '4px 8px', borderRadius: 8, border: '0.5px solid var(--tg-separator)', background: 'var(--tg-section-bg)', color: 'var(--tg-text)', fontSize: 13 };

  return (
    <>
      <Section header="Фильтры">
        <div style={{ padding: 'var(--space-sm) var(--space-lg)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select style={selectStyle} value={filterExch} onChange={(e) => setFilterExch(e.target.value)}>
            <option value="all">Все биржи</option>
            {EXCHANGES.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
          </select>
          <select style={selectStyle} value={filterTf} onChange={(e) => setFilterTf(e.target.value)}>
            <option value="all">Все TF</option>
            {TIMEFRAMES.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
          </select>
          <select style={selectStyle} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">Все статусы</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
          <Button variant="secondary" onClick={exportCsv} style={{ fontSize: 12 }}>CSV</Button>
        </div>
      </Section>

      {closed.length > 0 && (
        <Section header="Статистика">
          <div style={{ padding: 'var(--space-md) var(--space-lg)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              ['Win Rate', `${winRate.toFixed(1)}%`],
              ['Total P&L', `$${totalPnl.toFixed(2)}`],
              ['Trades', String(closed.length)],
              ['Wins', String(wins.length)],
            ].map(([k, v]) => (
              <div key={k} style={{ background: 'var(--tg-bg)', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontSize: 11, color: 'var(--tg-hint)' }}>{k}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: k === 'Total P&L' ? (totalPnl >= 0 ? '#34c759' : 'var(--tg-destructive)') : 'var(--tg-text)' }}>{v}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {filtered.length === 0 ? (
        <Empty icon="📋" title="Нет сделок" hint="Сделки появятся после срабатывания первого сигнала." />
      ) : (
        <Section header={`Сделки (${filtered.length})`}>
          {filtered.map((t) => (
            <div key={t.id} style={{ padding: 'var(--space-sm) var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>
                  {t.symbol} <span style={{ color: t.side === 'buy' ? '#34c759' : 'var(--tg-destructive)' }}>{t.side.toUpperCase()}</span>
                </span>
                <span style={{ fontSize: 12, color: 'var(--tg-hint)' }}>{t.exchange} / {t.timeframe}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--tg-hint)', marginTop: 2 }}>
                Entry: {fmtNum(t.entry_price, 4)} · SL: {fmtNum(t.stop_loss, 4)} · TP: {fmtNum(t.take_profit, 4)}
              </div>
              {t.status === 'closed' && (
                <div style={{ fontSize: 12, marginTop: 2 }}>
                  Exit: {fmtNum(t.exit_price, 4)} ({t.exit_reason}) ·{' '}
                  <span style={{ color: (t.pnl_usd ?? 0) >= 0 ? '#34c759' : 'var(--tg-destructive)' }}>
                    {t.pnl_usd != null ? `$${fmtNum(t.pnl_usd)}` : '—'} ({fmtNum(t.pnl_pct)}%)
                  </span>
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--tg-hint)', marginTop: 2 }}>{fmtDt(t.opened_at)}</div>
            </div>
          ))}
        </Section>
      )}
    </>
  );
}

// ── Logs tab ─────────────────────────────────────────────────────────────────

function LogsTab({ userId }: { userId: string }): JSX.Element {
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [search, setSearch] = useState('');

  const logs = useSupabaseQuery<BotLog[]>(
    () =>
      sb
        .from('bot_logs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(500),
    [userId],
    5_000,
  );

  const all = logs.data ?? [];
  const filtered = all.filter((l) => {
    if (filterLevel !== 'all' && l.level !== filterLevel) return false;
    if (filterSource !== 'all' && l.source !== filterSource) return false;
    if (search && !l.message.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const sources = [...new Set(all.map((l) => l.source))].sort();

  const selectStyle: React.CSSProperties = { padding: '4px 8px', borderRadius: 8, border: '0.5px solid var(--tg-separator)', background: 'var(--tg-section-bg)', color: 'var(--tg-text)', fontSize: 13 };

  if (logs.isLoading) return <Spinner />;

  return (
    <>
      <Section header="Фильтры">
        <div style={{ padding: 'var(--space-sm) var(--space-lg)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select style={selectStyle} value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)}>
            <option value="all">Все уровни</option>
            {['debug', 'info', 'warn', 'error'].map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </select>
          <select style={selectStyle} value={filterSource} onChange={(e) => setFilterSource(e.target.value)}>
            <option value="all">Все источники</option>
            {sources.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={{ padding: '0 var(--space-lg) var(--space-sm)' }}>
          <input
            type="text"
            placeholder="Поиск по тексту…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 10px', borderRadius: 8, border: '0.5px solid var(--tg-separator)', background: 'var(--tg-section-bg)', color: 'var(--tg-text)', fontSize: 13 }}
          />
        </div>
      </Section>

      {filtered.length === 0 ? (
        <Empty icon="📋" title="Логи пусты" hint="Логи появятся после запуска бота." />
      ) : (
        <Section header={`Логи (${filtered.length})`}>
          {filtered.map((l) => (
            <div key={l.id} style={{ padding: '6px var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)', fontSize: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ color: levelColor(l.level), fontWeight: 700, minWidth: 40 }}>{l.level.toUpperCase()}</span>
                <span style={{ color: 'var(--tg-hint)', fontSize: 11 }}>{l.source}</span>
                {l.exchange && <span style={{ color: 'var(--tg-hint)', fontSize: 11 }}>{l.exchange}</span>}
                {l.symbol && <span style={{ color: 'var(--tg-hint)', fontSize: 11 }}>{l.symbol}</span>}
                <span style={{ marginLeft: 'auto', color: 'var(--tg-hint)', fontSize: 11, whiteSpace: 'nowrap' }}>
                  {fmtDt(l.created_at)}
                </span>
              </div>
              <div style={{ color: 'var(--tg-text)', marginTop: 2 }}>{l.message}</div>
              {l.payload != null && (
                <details style={{ marginTop: 2 }}>
                  <summary style={{ color: 'var(--tg-hint)', cursor: 'pointer' }}>payload</summary>
                  <pre style={{ fontSize: 10, color: 'var(--tg-hint)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
                    {JSON.stringify(l.payload, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </Section>
      )}
    </>
  );
}

// ── Weex Options tab ──────────────────────────────────────────────────────────

function OptionsTab(): JSX.Element {
  const [filterSymbol, setFilterSymbol] = useState('all');

  const snapshots = useSupabaseQuery<WeexOptionsSnapshot[]>(
    () =>
      sb
        .from('weex_options_snapshots')
        .select('*')
        .order('taken_at', { ascending: false })
        .limit(300),
    [],
    30_000,
  );

  const all = snapshots.data ?? [];
  const filtered = filterSymbol === 'all' ? all : all.filter((s) => s.symbol === filterSymbol);
  const symbols = [...new Set(all.map((s) => s.symbol))].sort();

  const selectStyle: React.CSSProperties = { padding: '4px 8px', borderRadius: 8, border: '0.5px solid var(--tg-separator)', background: 'var(--tg-section-bg)', color: 'var(--tg-text)', fontSize: 13 };

  if (snapshots.isLoading) return <Spinner />;

  return (
    <>
      <Section header="Фильтр по символу">
        <div style={{ padding: 'var(--space-sm) var(--space-lg)' }}>
          <select style={selectStyle} value={filterSymbol} onChange={(e) => setFilterSymbol(e.target.value)}>
            <option value="all">Все символы</option>
            {symbols.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </Section>

      {filtered.length === 0 ? (
        <Empty icon="📊" title="Нет снэпшотов" hint="Снэпшоты опционов Weex появятся при срабатывании сигнала по активам из списка." />
      ) : (
        <Section header={`Снэпшоты (${filtered.length})`}>
          {filtered.map((snap) => (
            <div key={snap.id} style={{ padding: 'var(--space-sm) var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{snap.symbol} <span style={{ color: 'var(--tg-button)' }}>{snap.snapshot_t}</span></span>
                <span style={{ fontSize: 11, color: 'var(--tg-hint)' }}>{fmtDt(snap.taken_at)}</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ fontSize: 11, borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr style={{ color: 'var(--tg-hint)' }}>
                      <th style={{ textAlign: 'left', paddingRight: 8 }}>Strike</th>
                      <th style={{ paddingRight: 8 }}>C Bid</th>
                      <th style={{ paddingRight: 8 }}>C Ask</th>
                      <th style={{ paddingRight: 8 }}>P Bid</th>
                      <th>P Ask</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snap.strikes_json.map((row) => (
                      <tr key={row.strike}>
                        <td style={{ paddingRight: 8 }}>{row.strike}</td>
                        <td style={{ paddingRight: 8 }}>{fmtNum(row.call_bid)}</td>
                        <td style={{ paddingRight: 8 }}>{fmtNum(row.call_ask)}</td>
                        <td style={{ paddingRight: 8 }}>{fmtNum(row.put_bid)}</td>
                        <td>{fmtNum(row.put_ask)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {snap.signal_id && (
                <div style={{ fontSize: 11, color: 'var(--tg-hint)', marginTop: 4 }}>signal #{snap.signal_id}</div>
              )}
            </div>
          ))}
        </Section>
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

/** Раздел Beta — Торговый бот (FxPro · Vantage · Weex). */
export function BetaPage(): JSX.Element {
  const { userId } = useAuth();
  const [tab, setTab] = useState<TabId>('overview');

  if (!userId) {
    return (
      <Page title="Beta">
        <Empty icon="🔐" title="Не авторизован" hint="Откройте приложение через Telegram." />
      </Page>
    );
  }

  return (
    <Page title="Beta">
      <TabBar active={tab} onChange={setTab} />
      {tab === 'overview' && <OverviewTab userId={userId} onNavigateToSettings={() => setTab('settings')} />}
      {tab === 'settings' && <SettingsTab userId={userId} />}
      {tab === 'trades'   && <TradesTab userId={userId} />}
      {tab === 'logs'     && <LogsTab userId={userId} />}
      {tab === 'options'  && <OptionsTab />}
    </Page>
  );
}
